import type { ReactElement } from "react";

import type { GitHubAgentCapability } from "../../githubAgentCapability";
import { cn } from "../../lib/utils";
import { GitHubIcon } from "../Icons";
import { PluginConnectionList } from "../plugins/PluginConnectionList";
import {
  sortedPlugins,
  type ForgePlugin,
  type ForgePluginId,
  type PluginConnectionAttempt,
} from "../plugins/pluginsPage.logic";
import type { PluginConnectionAttempts } from "../plugins/pluginConnections";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";

type ConnectedAppTone = "ready" | "attention" | "later";

interface GitHubConnectedAppSetupItem {
  readonly id: "github";
  readonly name: string;
  readonly description: string;
  readonly statusLabel: string | null;
  readonly statusTone: ConnectedAppTone;
  readonly actionLabel: string;
  readonly actionHref: string | null;
  readonly actionKind?: "connect" | "manage";
  readonly icon: ReactElement;
}

function resolveGitHubSetupItem(
  capability: GitHubAgentCapability | null,
): GitHubConnectedAppSetupItem {
  if (capability?.requiresCmmdAuth) {
    return {
      id: "github",
      name: "GitHub",
      description: "Sign in again so Forge can check repository access.",
      statusLabel: "Sign in",
      statusTone: "attention",
      actionLabel: "Sign in",
      actionHref: null,
      actionKind: "connect",
      icon: <GitHubIcon className="size-5" />,
    };
  }

  if (capability?.connected && capability.repoAccessUsable) {
    const repoSummary =
      capability.repoCount === null
        ? "Forge can access your GitHub repositories."
        : `Forge can see ${capability.repoCount} repositor${
            capability.repoCount === 1 ? "y" : "ies"
          }${capability.login ? ` as ${capability.login}` : ""}.`;
    return {
      id: "github",
      name: "GitHub",
      description: repoSummary,
      statusLabel: "Ready",
      statusTone: "ready",
      actionLabel: "Manage",
      actionHref: null,
      actionKind: "manage",
      icon: <GitHubIcon className="size-5" />,
    };
  }

  if (capability?.connected) {
    return {
      id: "github",
      name: "GitHub",
      description:
        capability.error ??
        "GitHub is connected, but Forge cannot confirm repository import access yet.",
      statusLabel: "Limited",
      statusTone: "attention",
      actionLabel: "Fix access",
      actionHref: null,
      actionKind: "connect",
      icon: <GitHubIcon className="size-5" />,
    };
  }

  return {
    id: "github",
    name: "GitHub",
    description: "Connect GitHub so Forge can import and clone repositories.",
    statusLabel: "Not connected",
    statusTone: "attention",
    actionLabel: "Connect",
    actionHref: null,
    actionKind: "connect",
    icon: <GitHubIcon className="size-5" />,
  };
}

function connectedAppStatusClass(tone: ConnectedAppTone): string {
  switch (tone) {
    case "ready":
      return "border-success-border bg-success-surface text-foreground";
    case "attention":
      return "border-primary/35 bg-primary/10 text-foreground";
    case "later":
      return "border-border bg-muted/30 text-muted-foreground";
  }
}

function ConnectedAppSetupRow(props: {
  readonly app: GitHubConnectedAppSetupItem;
  readonly priority: "primary" | "secondary";
  readonly actionBusy?: boolean;
  readonly actionHref?: string | null;
  readonly actionLoading?: boolean;
  readonly onGitHubConnect?: () => void;
}): ReactElement {
  const connectAction =
    props.app.actionKind === "manage" && props.actionHref ? (
      <a
        className="inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-md border border-border bg-background px-3 text-sm font-semibold text-foreground transition-colors hover:border-foreground/25 hover:bg-muted/35"
        href={props.actionHref}
        rel="noreferrer"
        target="_blank"
      >
        {props.app.actionLabel}
      </a>
    ) : props.app.actionKind === "connect" && props.onGitHubConnect ? (
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={props.actionBusy || props.actionLoading}
        onClick={props.onGitHubConnect}
      >
        {props.actionBusy || props.actionLoading ? "Preparing..." : props.app.actionLabel}
      </Button>
    ) : null;

  return (
    <div
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-t border-border px-4",
        props.priority === "primary" ? "min-h-24 py-4" : "min-h-16 py-3",
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={cn(
            "grid shrink-0 place-items-center rounded-md border border-border bg-background text-foreground",
            props.priority === "primary" ? "size-10" : "size-9 opacity-80",
          )}
        >
          {props.app.icon}
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-foreground">{props.app.name}</h4>
            {props.app.statusLabel ? (
              <span
                className={cn(
                  "rounded-md border px-1.5 py-0.5 text-[10px] font-semibold",
                  connectedAppStatusClass(props.app.statusTone),
                )}
              >
                {props.app.statusLabel}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{props.app.description}</p>
        </div>
      </div>
      {connectAction ??
        (props.app.actionHref ? (
          <a
            className="inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-md border border-border bg-background px-3 text-sm font-semibold text-foreground transition-colors hover:border-foreground/25 hover:bg-muted/35"
            href={props.app.actionHref}
          >
            {props.app.actionLabel}
          </a>
        ) : (
          <span className="text-xs font-medium text-muted-foreground">{props.app.actionLabel}</span>
        ))}
    </div>
  );
}

function ConnectedAppSetupRowSkeleton(props: {
  readonly priority: "primary" | "secondary";
  readonly label: string;
  readonly name: string;
}): ReactElement {
  return (
    <div
      aria-label={props.label}
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-t border-border px-4",
        props.priority === "primary" ? "min-h-24 py-4" : "min-h-16 py-3",
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <Skeleton
          className={cn(
            "shrink-0 rounded-md border border-border/50",
            props.priority === "primary" ? "size-10" : "size-9",
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-foreground">{props.name}</h4>
            <Skeleton className="h-5 w-12 rounded-md" />
          </div>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">Checking access...</p>
        </div>
      </div>
      <Skeleton className="h-9 w-20 rounded-md" />
    </div>
  );
}

export function FirstAccountSetupAppAccessPanel(props: {
  readonly githubCapability: GitHubAgentCapability | null;
  readonly githubCapabilityLoading: boolean;
  readonly githubActionBusy: boolean;
  readonly githubAuthorizeUrl: string | null;
  readonly githubAuthorizeUrlLoading: boolean;
  readonly plugins: ReadonlyArray<ForgePlugin>;
  readonly pluginsLoading: boolean;
  readonly pluginsError: string | null;
  readonly pluginConnectionAttempt: PluginConnectionAttempt | null;
  readonly pluginConnectionAttempts: PluginConnectionAttempts;
  readonly onGitHubConnect: () => void;
  readonly onPluginConnect: (pluginId: ForgePluginId) => void;
}): ReactElement {
  const plugins = sortedPlugins(props.plugins).filter((plugin) => plugin.id !== "github");
  return (
    <section className="mt-6 overflow-hidden rounded-lg border border-border bg-background/70">
      <div className="px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">App access</h3>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Connect apps so Forge can import repositories and use project context.
        </p>
      </div>
      <div className="border-t border-border px-4 pt-4 pb-2 text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        Recommended
      </div>
      {props.githubCapabilityLoading ? (
        <ConnectedAppSetupRowSkeleton
          label="Loading GitHub connection"
          name="GitHub"
          priority="primary"
        />
      ) : (
        <ConnectedAppSetupRow
          app={resolveGitHubSetupItem(props.githubCapability)}
          priority="primary"
          actionBusy={props.githubActionBusy || props.pluginConnectionAttempts.github !== undefined}
          actionHref={props.githubAuthorizeUrl}
          actionLoading={props.githubAuthorizeUrlLoading}
          onGitHubConnect={props.onGitHubConnect}
        />
      )}
      <div className="border-t border-border px-4 pt-4 pb-2 text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        Plugins
      </div>
      <PluginConnectionList
        plugins={plugins}
        loading={props.pluginsLoading}
        error={props.pluginsError}
        attempts={props.pluginConnectionAttempts}
        attempt={props.pluginConnectionAttempt}
        onConnect={props.onPluginConnect}
        detailsLink={false}
      />
    </section>
  );
}
